/**
 * Blog Page Components
 * Refactored to use shared Layout and external article data
 */

import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import "./Blog.css";
import { 
  ArrowRight,
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Tag,
  Search,
  BookOpen,
  Target,
  Linkedin,
  Eye,
  Share2,
  Bookmark,
  Twitter,
  Facebook,
  Mail,
  Link as LinkIcon,
  Sparkles,
  CheckCircle2,
  Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";

// Import article data from external file
import { articles, categories } from "@/data/blogArticles";

// Newsletter Subscription Component
const NewsletterForm = ({ compact = false }) => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle, loading, success, error
  const [message, setMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }
    
    setStatus("loading");
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setStatus("success");
        setMessage(data.message);
        setEmail("");
      } else {
        setStatus("error");
        setMessage(data.message || "Failed to subscribe. Please try again.");
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setStatus("error");
      setMessage("Failed to subscribe. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div className={`newsletter-success ${compact ? 'compact' : ''}`}>
        <CheckCircle2 size={compact ? 20 : 24} />
        <span>{message}</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`newsletter-form-component ${compact ? 'compact' : ''}`}>
      <div className="newsletter-input-wrapper">
        <input 
          type="email" 
          placeholder="Your email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === "error") setStatus("idle");
          }}
          disabled={status === "loading"}
        />
        <Button 
          type="submit" 
          className="btn-primary-small"
          disabled={status === "loading"}
        >
          {status === "loading" ? (
            <Loader2 size={16} className="spinner" />
          ) : (
            "Subscribe"
          )}
        </Button>
      </div>
      {status === "error" && (
        <span className="newsletter-error">{message}</span>
      )}
    </form>
  );
};

// Article Card Component
const ArticleCard = ({ article, featured = false }) => {
  const IconComponent = article.icon;
  
  return (
    <Link to={`/blog/${article.id}`} className={`article-card ${featured ? 'featured' : ''}`} data-testid={`article-card-${article.id}`}>
      <div className="article-card-icon">
        <IconComponent size={featured ? 32 : 24} />
      </div>
      <div className="article-card-content">
        <span className="article-category">{article.category}</span>
        <h3>{article.title}</h3>
        <p>{article.excerpt}</p>
        <div className="article-meta">
          <span className="article-author">
            <User size={14} />
            {article.author}
          </span>
          <span className="article-date">
            <Calendar size={14} />
            {article.date}
          </span>
          <span className="article-read-time">
            <Clock size={14} />
            {article.readTime}
          </span>
        </div>
      </div>
      <div className="article-card-arrow">
        <ArrowRight size={20} />
      </div>
    </Link>
  );
};

// Blog Listing Component
const BlogListing = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 9;
  
  const featuredArticle = articles.find(a => a.featured);
  const regularArticles = articles.filter(a => !a.featured);
  
  // Filter articles
  const filteredArticles = regularArticles.filter(article => {
    const matchesSearch = 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = selectedCategory === "All" || article.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });
  
  // Pagination
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);
  const startIndex = (currentPage - 1) * articlesPerPage;
  const paginatedArticles = filteredArticles.slice(startIndex, startIndex + articlesPerPage);
  
  // Recent articles for sidebar
  const recentArticles = articles.slice(0, 5);
  
  // Popular tags
  const allTags = articles.flatMap(a => [a.category]);
  const tagCounts = allTags.reduce((acc, tag) => {
    acc[tag] = (acc[tag] || 0) + 1;
    return acc;
  }, {});
  const popularTags = Object.entries(tagCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([tag]) => tag);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  return (
    <Layout>
      <div className="blog-page" data-testid="blog-page">
        <SEO 
          title="Blog - Marketing Insights & Strategies"
          description="Explore IntentLogic's blog for expert insights on predictive marketing, AI, data strategy, and consumer intelligence. Stay ahead with the latest trends."
          keywords="marketing blog, predictive marketing, AI marketing, data strategy, consumer intelligence, digital marketing insights, marketing analytics"
          canonical="/blog"
        />
        
        {/* Hero Section */}
        <section className="blog-hero">
          <div className="blog-hero-content">
            <span className="blog-label">INTENTLOGIC BLOG</span>
            <h1>Marketing Intelligence Insights</h1>
            <p>Expert perspectives on predictive marketing, data strategy, and the future of customer acquisition.</p>
          </div>
        </section>

        {/* Filters Bar */}
        <div className="blog-filters-bar">
          <div className="blog-filters-content">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                data-testid="blog-search"
              />
            </div>
            <div className="category-pills">
              {categories.slice(0, 5).map(category => (
                <button
                  key={category}
                  className={`category-pill ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
              <select 
                className="category-dropdown"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="blog-content-wrapper">
          {/* Featured Article */}
          {selectedCategory === "All" && !searchTerm && featuredArticle && (
            <section className="featured-section">
              <h2 className="section-title">Featured Article</h2>
              <ArticleCard article={featuredArticle} featured />
            </section>
          )}

          {/* Articles Grid */}
          <section className="articles-section">
            <h2 className="section-title">
              {selectedCategory === "All" ? "All Articles" : selectedCategory}
              <span className="article-count">{filteredArticles.length} articles</span>
            </h2>
            
            {paginatedArticles.length > 0 ? (
              <>
                <div className="articles-grid">
                  {paginatedArticles.map(article => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="pagination">
                    <button 
                      className="pagination-btn"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ArrowLeft size={18} />
                      Previous
                    </button>
                    
                    <div className="pagination-numbers">
                      {[...Array(totalPages)].map((_, i) => (
                        <button
                          key={i}
                          className={`pagination-number ${currentPage === i + 1 ? 'active' : ''}`}
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                    
                    <button 
                      className="pagination-btn"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ArrowRight size={18} />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="no-articles">
                <p>No articles found matching your criteria.</p>
                <Button 
                  className="btn-secondary"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedCategory("All");
                  }}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </section>

          {/* Newsletter CTA */}
          <section className="blog-newsletter-cta">
            <div className="newsletter-cta-content">
              <Sparkles size={28} />
              <div className="newsletter-cta-text">
                <h3>Stay Updated</h3>
                <p>Get the latest marketing insights delivered to your inbox weekly.</p>
              </div>
              <NewsletterForm />
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
};

// Helper function to format markdown-like content to HTML with IDs for TOC
const formatContentWithIds = (content) => {
  let html = content
    // Headers with IDs
    .replace(/^## (.*$)/gim, (match, p1) => {
      const id = p1.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      return `<h2 id="${id}">${p1}</h2>`;
    })
    .replace(/^### (.*$)/gim, (match, p1) => {
      const id = p1.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      return `<h3 id="${id}">${p1}</h3>`;
    })
    // Bold text
    .replace(/^\*\*(.*)\*\*$/gim, '<p class="key-point"><strong>$1</strong></p>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic text
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Blockquotes
    .replace(/^> (.*$)/gim, '<blockquote>$1</blockquote>')
    // Horizontal rules with decorative style
    .replace(/---/g, '<hr class="section-divider" />')
    // Numbered lists
    .replace(/^\d+\. (.*$)/gim, '<oli>$1</oli>')
    // Bullet lists  
    .replace(/^- (.*$)/gim, '<uli>$1</uli>');
  
  // Process lists - wrap consecutive list items
  html = html.replace(/(<uli>.*?<\/uli>\n?)+/gs, (match) => {
    const items = match.replace(/<uli>(.*?)<\/uli>/g, '<li>$1</li>');
    return `<ul>${items}</ul>`;
  });
  
  html = html.replace(/(<oli>.*?<\/oli>\n?)+/gs, (match) => {
    const items = match.replace(/<oli>(.*?)<\/oli>/g, '<li>$1</li>');
    return `<ol>${items}</ol>`;
  });
  
  // Paragraphs
  html = html
    .replace(/\n\n/g, '</p><p>')
    .replace(/^(?!<[hupol]|<li|<bl)(.*$)/gim, '<p>$1</p>')
    .replace(/<p><\/p>/g, '')
    .replace(/<p>(<h[23])/g, '$1')
    .replace(/(<\/h[23]>)<\/p>/g, '$1')
    .replace(/<p>(<ul>)/g, '$1')
    .replace(/(<\/ul>)<\/p>/g, '$1')
    .replace(/<p>(<ol>)/g, '$1')
    .replace(/(<\/ol>)<\/p>/g, '$1')
    .replace(/<p>(<hr)/g, '$1')
    .replace(/(\/?>)<\/p>/g, '$1')
    .replace(/<p>(<blockquote>)/g, '$1')
    .replace(/(<\/blockquote>)<\/p>/g, '$1');

  return html;
};

// Extract headings for TOC
const extractHeadings = (content) => {
  const headings = [];
  const h2Regex = /^## (.+)$/gm;
  const h3Regex = /^### (.+)$/gm;
  
  let match;
  while ((match = h2Regex.exec(content)) !== null) {
    const text = match[1];
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({ level: 2, text, id });
  }
  
  while ((match = h3Regex.exec(content)) !== null) {
    const text = match[1];
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    headings.push({ level: 3, text, id });
  }
  
  // Sort by position in content
  headings.sort((a, b) => content.indexOf(`## ${a.text}`) - content.indexOf(`## ${b.text}`));
  
  return headings;
};

// Article Detail Component
const ArticleDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [readingProgress, setReadingProgress] = useState(0);
  const [activeHeading, setActiveHeading] = useState('');
  const articleRef = useRef(null);
  
  const article = articles.find(a => a.id === slug);
  
  // Track reading progress
  useEffect(() => {
    const handleScroll = () => {
      if (!articleRef.current) return;
      
      const element = articleRef.current;
      const windowHeight = window.innerHeight;
      const documentHeight = element.scrollHeight;
      const scrollTop = window.scrollY - element.offsetTop + windowHeight;
      const progress = Math.min(100, Math.max(0, (scrollTop / documentHeight) * 100));
      setReadingProgress(progress);
      
      // Update active heading for TOC
      const headings = element.querySelectorAll('h2, h3');
      let currentHeading = '';
      headings.forEach(heading => {
        const rect = heading.getBoundingClientRect();
        if (rect.top <= 100) {
          currentHeading = heading.id;
        }
      });
      setActiveHeading(currentHeading);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  
  if (!article) {
    return (
      <Layout>
        <div className="blog-page article-not-found">
          <h1>Article Not Found</h1>
          <p>The article you're looking for doesn't exist.</p>
          <Button className="btn-primary" onClick={() => navigate('/blog')}>
            <ArrowLeft size={18} />
            Back to Blog
          </Button>
        </div>
      </Layout>
    );
  }
  
  const IconComponent = article.icon;
  const headings = extractHeadings(article.content);
  const relatedArticles = articles
    .filter(a => a.category === article.category && a.id !== article.id)
    .slice(0, 3);
  
  const handleShare = (platform) => {
    const url = window.location.href;
    const title = article.title;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Check out this article: ${url}`)}`
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };
  
  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  return (
    <Layout>
      <div className="blog-page article-page" data-testid="article-page">
        <SEO 
          title={`${article.title} - IntentLogic Blog`}
          description={article.excerpt}
          keywords={`${article.category.toLowerCase()}, marketing insights, ${article.title.toLowerCase().split(' ').slice(0, 3).join(', ')}`}
          canonical={`/blog/${article.id}`}
        />
        
        {/* Reading Progress Bar */}
        <div className="reading-progress-bar" style={{ width: `${readingProgress}%` }} />
        
        {/* Article Header */}
        <header className="article-header">
          <div className="article-header-content">
            <Link to="/blog" className="back-to-blog">
              <ArrowLeft size={18} />
              Back to Blog
            </Link>
            <span className="article-category-badge">{article.category}</span>
            <h1>{article.title}</h1>
            <p className="article-excerpt">{article.excerpt}</p>
            <div className="article-meta-detailed">
              <div className="author-info">
                <div className="author-avatar">
                  <User size={20} />
                </div>
                <div>
                  <span className="author-name">{article.author}</span>
                  <span className="publish-date">{article.date}</span>
                </div>
              </div>
              <div className="article-stats">
                <span><Clock size={16} /> {article.readTime}</span>
                <span><Eye size={16} /> 2.4K views</span>
              </div>
            </div>
          </div>
        </header>

        {/* Three Column Layout */}
        <div className="article-layout">
          {/* Left Sidebar - Table of Contents */}
          <aside className="article-left-sidebar">
            <div className="toc-container">
              <h4>Table of Contents</h4>
              <nav className="toc-nav">
                {headings.map((heading, index) => (
                  <a
                    key={index}
                    href={`#${heading.id}`}
                    className={`toc-link ${heading.level === 3 ? 'toc-sublink' : ''} ${activeHeading === heading.id ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(heading.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    {heading.text}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Article Content */}
          <article className="article-content" ref={articleRef}>
            <div className="article-icon-large">
              <IconComponent size={48} />
            </div>
            
            <div 
              className="article-body"
              dangerouslySetInnerHTML={{ __html: formatContentWithIds(article.content) }}
            />
            
            <div className="article-footer-actions">
              <div className="article-share-inline">
                <span>Share this article:</span>
                <div className="share-buttons-inline">
                  <button onClick={() => handleShare('twitter')} title="Share on Twitter">
                    <Twitter size={18} />
                  </button>
                  <button onClick={() => handleShare('linkedin')} title="Share on LinkedIn">
                    <Linkedin size={18} />
                  </button>
                  <button onClick={() => handleShare('facebook')} title="Share on Facebook">
                    <Facebook size={18} />
                  </button>
                  <button onClick={copyLink} title="Copy Link">
                    <LinkIcon size={18} />
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Right Sidebar */}
          <aside className="article-right-sidebar">
            {/* Author Box */}
            <div className="sidebar-author-box">
              <div className="author-avatar-large">
                <User size={32} />
              </div>
              <h4>{article.author}</h4>
              <p className="author-title">Research Team Lead</p>
              <p className="author-bio">
                Expert in predictive marketing, consumer data analytics, and AI-driven campaign optimization with 10+ years in the industry.
              </p>
              <Link to="/about" className="author-link">
                View all articles <ArrowRight size={14} />
              </Link>
            </div>

            {/* Share Box */}
            <div className="sidebar-share-box">
              <h4>Share Article</h4>
              <div className="share-buttons">
                <button className="share-btn twitter" onClick={() => handleShare('twitter')}>
                  <Twitter size={18} />
                  <span>Twitter</span>
                </button>
                <button className="share-btn linkedin" onClick={() => handleShare('linkedin')}>
                  <Linkedin size={18} />
                  <span>LinkedIn</span>
                </button>
                <button className="share-btn facebook" onClick={() => handleShare('facebook')}>
                  <Facebook size={18} />
                  <span>Facebook</span>
                </button>
                <button className="share-btn email" onClick={() => handleShare('email')}>
                  <Mail size={18} />
                  <span>Email</span>
                </button>
                <button className="share-btn copy" onClick={copyLink}>
                  <LinkIcon size={18} />
                  <span>Copy Link</span>
                </button>
              </div>
            </div>

            {/* Newsletter Box */}
            <div className="sidebar-newsletter-box">
              <Sparkles size={24} />
              <h4>Stay Updated</h4>
              <p>Get the latest insights on predictive marketing delivered weekly.</p>
              <NewsletterForm compact />
            </div>

            {/* Quick Stats */}
            <div className="sidebar-stats-box">
              <h4>Article Stats</h4>
              <div className="quick-stats-grid">
                <div className="quick-stat">
                  <Eye size={16} />
                  <span className="stat-value">2.4K</span>
                  <span className="stat-label">Views</span>
                </div>
                <div className="quick-stat">
                  <Clock size={16} />
                  <span className="stat-value">{article.readTime.replace(' read', '')}</span>
                  <span className="stat-label">Read Time</span>
                </div>
                <div className="quick-stat">
                  <Share2 size={16} />
                  <span className="stat-value">148</span>
                  <span className="stat-label">Shares</span>
                </div>
                <div className="quick-stat">
                  <Bookmark size={16} />
                  <span className="stat-value">89</span>
                  <span className="stat-label">Saves</span>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Author/CTA Box - Full Width */}
        <div className="article-cta-box">
          <div className="cta-box-content">
            <div className="cta-box-icon">
              <Target size={32} />
            </div>
            <div className="cta-box-text">
              <h3>Ready to implement these strategies?</h3>
              <p>Schedule a consultation with our team to discuss how IntentLogic can help drive measurable results for your business.</p>
            </div>
            <Button className="btn-primary-zeta" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              Schedule Consultation
              <ArrowRight size={18} />
            </Button>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="related-articles">
            <div className="related-header">
              <h2>Related Articles</h2>
              <Link to="/blog" className="view-all-link">
                View all articles <ArrowRight size={16} />
              </Link>
            </div>
            <div className="related-grid">
              {relatedArticles.map(a => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
};

// Main Blog Page Component (handles routing internally)
function BlogPage() {
  const { slug } = useParams();

  return slug ? <ArticleDetail /> : <BlogListing />;
}

export default BlogPage;
